import { TournamentInvitation } from '../models';
import { TournamentInvitationRepository } from '../repositories/TournamentInvitationRepository';
import { TournamentRepository, ParticipantRepository } from '../repositories';
import { ModerateContentUseCase } from './ModerateContentUseCase';
import { logger } from '@/src/core/utils';
import { ABSOLUTE_MAX_PARTICIPANTS } from '../models/PlanLimits';

export class SendTournamentInvitationUseCase {
  constructor(
    private invitationRepository: TournamentInvitationRepository,
    private tournamentRepository: TournamentRepository,
    private participantRepository: ParticipantRepository,
    private moderateContent: ModerateContentUseCase
  ) {}

  async execute(params: {
    tournamentId: string;
    inviterId: string;
    inviteeId: string;
    message?: string;
  }): Promise<TournamentInvitation> {
    const { tournamentId, inviterId, inviteeId, message } = params;

    logger.debug('SendInvitation', 'Sending invitation', { tournamentId, inviteeId });

    const tournament = await this.tournamentRepository.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found');
    }

    if (tournament.status !== 'setup') {
      throw new Error('Tournament is no longer accepting participants');
    }

    // Check if invitee is already a participant
    const participants = await this.participantRepository.getByTournamentId(tournamentId);
    if (participants.some((p) => p.userId === inviteeId)) {
      throw new Error('User is already a participant in this tournament');
    }

    // Check max participants
    if (tournament.maxParticipants && participants.length >= tournament.maxParticipants) {
      throw new Error('Tournament is full');
    }
    if (participants.length >= ABSOLUTE_MAX_PARTICIPANTS) {
      throw new Error('Tournament has reached the maximum allowed participants');
    }

    // Check for existing pending invitation
    const existing = await this.invitationRepository.getByTournamentAndUser(tournamentId, inviteeId);
    if (existing) {
      throw new Error('User has already been invited to this tournament');
    }

    // Moderate message if provided
    if (message) {
      await this.moderateContent.execute(message, 'challenge_message', inviterId);
    }

    const invitation = await this.invitationRepository.send(tournamentId, inviterId, inviteeId, message);

    logger.debug('SendInvitation', 'Invitation sent', { invitationId: invitation.id });

    return invitation;
  }
}
